import React, { useState } from "react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";
import { generateImageFromPromptStreaming } from "../handlers/ImageGen";
import { useAuth } from "../contexts/AuthContext";

interface Props {
  onSelectImage: (imageUrl: string) => void;
}

const TextToImage: React.FC<Props> = ({ onSelectImage }) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<(string | null)[]>([]);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  // toasts handled via sonner

  const { currentUser, signInWithGoogle } = useAuth();

  const onGenerate = async () => {
    if (!prompt || loading) return;

    let user = currentUser;
    if (!user) {
      try {
        user = await signInWithGoogle();
      } catch (error) {
        toast("Failed to sign in with Google");
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!user) {
        toast("Authentication failed");
        return;
      }
    }

    setLoading(true);
    setShowPlaceholders(true);
    // Initialize with 4 null placeholders for loading state
    setImageUrls([null, null, null, null]);
    
    try {
      // Use streaming API to generate images progressively
      await generateImageFromPromptStreaming(prompt, true, {
        onImageReceived: (index, url, metadata) => {
          // Update the specific image slot as it arrives
          setImageUrls(prev => {
            const newUrls = [...prev];
            newUrls[index] = url;
            return newUrls;
          });
          
          // Log warning if background removal failed
          if (metadata?.backgroundRemovalError) {
            console.warn(`Background removal failed for image ${index + 1}:`, metadata.backgroundRemovalError);
            // Optionally show a toast, but not too intrusive since the image still works
            // toast.warning(`Image ${index + 1}: Background removal unavailable`);
          }
        },
        onError: (index, error) => {
          // Handle individual image errors
          console.error(`Error generating image ${index + 1}:`, error);
          // Optionally show error state for that specific image
          setImageUrls(prev => {
            const newUrls = [...prev];
            // Keep it as null or set a special error state
            newUrls[index] = null;
            return newUrls;
          });
        },
        onComplete: () => {
          console.log("All images processed");
          setLoading(false);
        }
      });
    } catch (error) {
      toast("Failed to generate images");
      console.error("Image generation error:", error);
      setShowPlaceholders(false);
      setImageUrls([]);
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 space-y-4">
      {/* toasts handled globally */}
      <div>
        <Textarea
          placeholder="Enter prompt"
          rows={5}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={onGenerate} disabled={loading || !prompt}>
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-main border-t-transparent rounded-full animate-spin" />
              Generating...
            </span>
          ) : (
            "Generate"
          )}
        </Button>
      </div>

      {showPlaceholders && imageUrls.length > 0 && (
        <div className="mt-4">
          <p className="text-foreground">Result:</p>
          <div className="grid grid-cols-2 gap-4">
            {imageUrls.map((url, index) => (
              <div key={index} className="relative min-h-[120px]">
                {url ? (
                  <>
                    <img
                      src={url}
                      alt={`Generated ${index}`}
                      className="w-full h-auto rounded-md animate-fadeIn border-2 border-border"
                    />
                    <button
                      onClick={() => onSelectImage(url)}
                      className="absolute bottom-2 right-2 border-2 border-border bg-main text-[color:var(--color-main-foreground)] px-2 py-1 rounded-md text-xs neo-shadow hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    >
                      Make 3D
                    </button>
                  </>
                ) : (
                  <Skeleton className="w-full h-full min-h-[120px] flex items-center justify-center">
                    <span className="inline-block w-8 h-8 border-2 border-main border-t-transparent rounded-full animate-spin" />
                  </Skeleton>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextToImage;
